import { useState } from 'react';
import { api } from '@/api/client';
import { CommunicationsResponse } from '@/api/types';
import { handleAppError } from '@/lib/errorUtils';
import { AIGenerationHookParams } from './ai/types';

export function useCommunications({
  workspaceId,
  selectedId,
  jobText,
  targetDetails,
  setStatus,
  setStatusMessage,
  recordUsage,
  setRightView,
  getSignal,
  setIsGenerating,
  scrollToResults,
}: AIGenerationHookParams) {
  const [communicationsResult, setCommunicationsResult] = useState<CommunicationsResponse | null>(null);

  const handleGenerateCommunications = async (emailTypeOrSkip?: string | boolean, skipStateUpdatesFallback?: boolean) => {
    const emailType = typeof emailTypeOrSkip === 'string' ? emailTypeOrSkip : 'Initial Application Follow-up';
    const isSkip = (typeof emailTypeOrSkip === 'boolean' ? emailTypeOrSkip : skipStateUpdatesFallback) === true;
    if (!selectedId || jobText.trim().length < 50) {
      if (!isSkip) {
        setStatus('error');
        setStatusMessage('Please enter a longer, meaningful job description.');
      }
      return;
    }
    
    setIsGenerating(prev => ({ ...prev, communications: true }));
    if (!isSkip) {
      setStatus('idle');
      setRightView('communications');
      scrollToResults();
    }

    try {
      if (!isSkip) await new Promise(r => setTimeout(r, 300));
      const signal = isSkip ? getSignal('package') : getSignal('communications');
      const result = await api.generateCommunications(workspaceId, { resumeId: selectedId, jobText, emailType, ...targetDetails }, { signal });
      if (result.usage) {
        recordUsage(result.usage.cost, result.usage.promptTokens, result.usage.completionTokens);
      }
      
      setCommunicationsResult(result);
      if (!isSkip) setStatusMessage('Outreach ready');
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (!isSkip) {
        setStatus('error');
        setStatusMessage(handleAppError(err, 'Generation failed'));
      }
    } finally {
      setIsGenerating(prev => ({ ...prev, communications: false }));
    }
  };

  return { communicationsResult, setCommunicationsResult, handleGenerateCommunications };
}
